import React from "react";

import { ageOnClose } from "../lib/eligibility";
import type { Category, Track } from "../lib/types";

/**
 * Shows every track, greying out the ones this applicant cannot have **with
 * the reason visible**.
 *
 * Hiding ineligible tracks instead of explaining them is the main avoidable
 * way to lose an applicant: they fill in four steps, submit, and only then
 * learn they were never eligible.
 */
export default function TrackPicker({
  categories,
  tracks,
  selectedId,
  onSelect,
  gender,
  dateOfBirth,
  closesAt,
}: {
  categories: Category[];
  tracks: Track[];
  selectedId: string;
  onSelect: (id: string) => void;
  gender: "male" | "female" | null;
  dateOfBirth: string | null;
  closesAt: string | null;
}) {
  const reasonFor = (track: Track): string | null => {
    if (gender && track.genderRestriction !== "any" && track.genderRestriction !== gender) {
      return track.genderRestriction === "female" ? "Women only" : "Men only";
    }
    if (dateOfBirth && (track.minAge != null || track.maxAge != null)) {
      const age = ageOnClose(dateOfBirth, closesAt);
      if (track.minAge != null && track.maxAge != null && (age < track.minAge || age > track.maxAge)) {
        return `Ages ${track.minAge}–${track.maxAge} only`;
      }
      if (track.minAge != null && age < track.minAge) return `Ages ${track.minAge} and over`;
      if (track.maxAge != null && age > track.maxAge) return `Ages up to ${track.maxAge}`;
    }
    return null;
  };

  return (
    <div className="space-y-5">
      {categories.map((category) => {
        const categoryTracks = tracks.filter((t) => t.categoryId === category.id);
        if (categoryTracks.length === 0) return null;

        return (
          <div key={category.id}>
            <div className="flex items-center gap-2">
              <span className="text-lg" aria-hidden="true">{category.icon}</span>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                {category.name}
              </h3>
            </div>

            <div className="mt-2 space-y-2">
              {categoryTracks.map((track) => {
                const reason = reasonFor(track);
                const disabled = reason !== null;
                const selected = selectedId === track.id;

                return (
                  <button
                    key={track.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => onSelect(track.id)}
                    aria-pressed={selected}
                    className={`flex w-full items-start justify-between gap-3 rounded-2xl border p-4 text-left transition ${
                      disabled
                        ? "cursor-not-allowed border-slate-200 bg-slate-50"
                        : selected
                          ? "border-blue-600 bg-blue-50"
                          : "border-slate-300 bg-white hover:border-blue-400"
                    }`}
                  >
                    <span className="min-w-0">
                      <span
                        className={`block font-medium ${
                          disabled ? "text-slate-400" : "text-slate-900"
                        }`}
                      >
                        {track.name}
                      </span>
                      <span
                        className={`mt-0.5 block text-xs ${
                          disabled ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        {track.awardTypes
                          .map((a) =>
                            a === "cash" ? "Money" : a === "equipment" ? "Equipment" : "Training",
                          )
                          .join(" + ")}
                      </span>
                    </span>

                    {reason ? (
                      <span className="shrink-0 rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                        {reason}
                      </span>
                    ) : selected ? (
                      <span className="shrink-0 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white">
                        Chosen
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
